import {MenuItem, TextField} from '@mui/material'
import React from 'react'

import {INPUT_FORM_FIELD_SX} from '../../consts'
import {ShiftEventCategory, TaskRepetitionUnit, taskRepetitionUnits} from '../../types'

export type CommonProps<T extends string | number> = {
  'data-test-id': string
  disabled?: boolean
  errorText?: React.ReactNode
  fullWidth?: boolean
  onChange: (newValue: T, event: React.ChangeEvent<HTMLInputElement>) => void
  required?: boolean
  value?: T
  hiddenOptions?: ReadonlyArray<T>
  translate: (key: string) => string
}

type Props<T extends string | number> = CommonProps<T> & {
  keyName: string
  options: ReadonlyArray<T>
  customTemplate?: (key: T) => React.ReactNode
}

export function CommonDropDown<T extends string | number>({
  keyName,
  options,
  customTemplate,
  hiddenOptions = [],
  disabled,
  errorText,
  fullWidth,
  onChange,
  required,
  value,
  'data-test-id': dataTestId,
  translate
}: Props<T>) {
  return (
    <TextField
      data-test-id={dataTestId}
      disabled={disabled}
      error={!!errorText}
      fullWidth={fullWidth ?? true}
      helperText={errorText}
      label={
        translate(`shiftEvent.label.${keyName}`) +
        (!required ? ` (${translate('shiftEvent.label.optional')})` : '')
      }
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
        onChange(event.target.value as T, event)
      }}
      required={required}
      select
      value={value ?? ''}
      variant="filled"
      inputProps={{'data-test-id': `${dataTestId}-input`}}
      InputProps={INPUT_FORM_FIELD_SX}
    >
      {options.map((option) => (
        <MenuItem
          key={option}
          value={option}
          sx={{display: hiddenOptions.includes(option) ? 'none' : 'block'}}
          data-test-id={`${dataTestId}-${option}`}
        >
          {customTemplate ? customTemplate(option) : translate(`shiftEvent.${keyName}.${option}`)}
        </MenuItem>
      ))}
    </TextField>
  )
}

export const CategoryDropDown: React.FC<CommonProps<ShiftEventCategory>> = (props) => {
  return (
    <CommonDropDown {...props} keyName="category" options={Object.values(ShiftEventCategory)} />
  )
}

export const TaskRepetitionDropDown: React.FC<CommonProps<TaskRepetitionUnit>> = (props) => {
  return <CommonDropDown {...props} keyName="recurring" options={taskRepetitionUnits} />
}

export const CustomTaskRepetitionDropDown: React.FC<CommonProps<'daily' | 'weekly'>> = (props) => {
  return <CommonDropDown {...props} keyName="recurring" options={['daily', 'weekly']} />
}

export const CustomTaskRepetitionDropDownYearly: React.FC<
  CommonProps<'daily' | 'weekly' | 'monthly' | 'yearly'>
> = (props) => {
  return (
    <CommonDropDown
      {...props}
      keyName="recurring"
      options={['daily', 'weekly', 'monthly', 'yearly']}
    />
  )
}
